import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalShipping as ShippingIcon,
  CallReceived as ReceiveIcon,
  Send as DispatchIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useAuth } from "../../../auth/hooks/useAuth";
import { DataTable, Column } from "../DataTable/DataTable";
import { stockTransferService } from "../../services/stock-transfer.service";
import {
  StockTransfer,
  StockTransferFilters,
  LocationOption,
  TRANSFER_TYPES,
  TRANSFER_STATUS,
} from "../../types/stock-transfer.types";

interface StockTransferListProps {
  onCreateTransfer: () => void;
  onEditTransfer: (transfer: StockTransfer) => void;
  onViewTransfer: (transfer: StockTransfer) => void;
}

export const StockTransferList: React.FC<StockTransferListProps> = ({
  onCreateTransfer,
  onEditTransfer,
  onViewTransfer,
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  // Data state
  const [transfers, setTransfers] = useState<StockTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Filter state
  const [filters, setFilters] = useState<StockTransferFilters>({
    transfer_type: "All",
    status: "All",
    search: "",
    date_range: {},
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Location options for filters
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);

  // Action states
  const [actionLoading, setActionLoading] = useState<{
    [transferId: string]: boolean;
  }>({});

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    action: () => {},
  });

  useEffect(() => {
    loadTransfers();
    loadLocations();
  }, [page, pageSize, sortField, sortOrder, filters]);

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const response = await stockTransferService.getStockTransfers(
        page + 1,
        pageSize,
        filters,
        sortField,
        sortOrder
      );

      if (response.success) {
        setTransfers(response.data.transfers);
        setTotalItems(response.data.total);
      }
    } catch (error) {
      console.error("Error loading transfers:", error);
      toast.error("Failed to load transfers");
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await stockTransferService.getLocations();
      if (response.success) {
        // Combine branches and warehouses into one array for the dropdown
        const { branches = [], warehouses = [] } = response.data;
        setLocationOptions([...branches, ...warehouses]);
      }
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };

  const handleFilterChange = (
    field: keyof StockTransferFilters,
    value: any
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleDateRangeChange = (
    field: "start" | "end",
    value: number | null
  ) => {
    setFilters((prev) => ({
      ...prev,
      date_range: {
        ...prev.date_range,
        [field]: value,
      },
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      transfer_type: "All",
      status: "All",
      search: "",
      date_range: {},
    });
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSort = (field: string, order: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(order);
    setPage(0);
  };

  const handleReceiveTransfer = async (transfer: StockTransfer) => {
    setActionLoading((prev) => ({ ...prev, [transfer._id]: true }));
    try {
      const response = await stockTransferService.receiveTransfer(
        transfer._id,
        {
          transfer_id: transfer._id,
          received_items: transfer.items.map((item) => ({
            item_id: item._id,
            received_quantity: item.quantity,
            condition: "Good" as const,
          })),
        }
      );

      if (response.success) {
        toast.success("Transfer received successfully");
        loadTransfers();
      }
    } catch (error) {
      console.error("Error receiving transfer:", error);
      toast.error("Failed to receive transfer");
    } finally {
      setActionLoading((prev) => ({ ...prev, [transfer._id]: false }));
    }
  };

  const handleDispatchTransfer = async (transfer: StockTransfer) => {
    setActionLoading((prev) => ({ ...prev, [transfer._id]: true }));
    try {
      const response = await stockTransferService.dispatchTransfer(
        transfer._id,
        {
          transfer_id: transfer._id,
        }
      );

      if (response.success) {
        toast.success("Transfer dispatched successfully");
        loadTransfers();
      }
    } catch (error) {
      console.error("Error dispatching transfer:", error);
      toast.error("Failed to dispatch transfer");
    } finally {
      setActionLoading((prev) => ({ ...prev, [transfer._id]: false }));
    }
  };

  const handleCancelTransfer = async (
    transfer: StockTransfer,
    reason: string
  ) => {
    setActionLoading((prev) => ({ ...prev, [transfer._id]: true }));
    try {
      const response = await stockTransferService.cancelTransfer(
        transfer._id,
        reason
      );

      if (response.success) {
        toast.success("Transfer cancelled successfully");
        loadTransfers();
      }
    } catch (error) {
      console.error("Error cancelling transfer:", error);
      toast.error("Failed to cancel transfer");
    } finally {
      setActionLoading((prev) => ({ ...prev, [transfer._id]: false }));
    }
  };

  const handleExport = async () => {
    try {
      const blob = await stockTransferService.exportTransfers(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock-transfers-${dayjs().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Export downloaded successfully");
    } catch (error) {
      console.error("Error exporting transfers:", error);
      toast.error("Failed to export transfers");
    }
  };

  const formatTransferSummary = (items: any[]): string => {
    if (!items || items.length === 0) return "No items";

    const visibleItems = items.slice(0, 2);
    const visibleNames = visibleItems.map((item) => item.item_name);

    if (items.length <= 2) {
      return visibleNames.join(", ");
    }

    const remainingCount = items.length - 2;
    return `${visibleNames.join(", ")} (+${remainingCount} more)`;
  };

  const getStatusColor = (status: string) => {
    const statusColors = {
      Draft: "default",
      Dispatched: "primary",
      "In Transit": "info",
      Received: "success",
      Cancelled: "error",
    } as const;
    return statusColors[status as keyof typeof statusColors] || "default";
  };

  const getTransferTypeIcon = (type: string) => {
    return type === "Inter-Branch" ? <ShippingIcon /> : <StoreIcon />;
  };

  const canUserReceiveTransfer = (transfer: StockTransfer): boolean => {
    // User can receive if they belong to the target branch and transfer is dispatched
    return (
      transfer.status === "Dispatched" &&
      user?.branch_id === transfer.target_location.branch_id
    );
  };

  const canUserDispatchTransfer = (transfer: StockTransfer): boolean => {
    // User can dispatch if they belong to the source branch and transfer is in draft
    return (
      transfer.status === "Draft" &&
      user?.branch_id === transfer.source_location.branch_id
    );
  };

  const canUserEditTransfer = (transfer: StockTransfer): boolean => {
    // User can edit if they created the transfer and it's still in draft
    return (
      transfer.status === "Draft" &&
      transfer.workflow.created_by === user?.user_id
    );
  };

  const columns: Column<StockTransfer>[] = [
    {
      id: "transfer_number",
      label: "Transfer ID",
      minWidth: 120,
      sortable: true,
      format: (value) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "primary.main" }}
        >
          {value}
        </Typography>
      ),
    },
    {
      id: "transfer_type",
      label: "Type",
      minWidth: 120,
      sortable: true,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getTransferTypeIcon(value)}
          <Typography variant="body2">{value}</Typography>
        </Box>
      ),
    },
    {
      id: "source_location",
      label: "Source",
      minWidth: 200,
      format: (value) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value.branch_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {value.warehouse_name}
          </Typography>
        </Box>
      ),
    },
    {
      id: "target_location",
      label: "Target",
      minWidth: 200,
      format: (value) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value.branch_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {value.warehouse_name}
          </Typography>
        </Box>
      ),
    },
    {
      id: "workflow",
      label: "Created By",
      minWidth: 150,
      format: (value) => (
        <Box>
          <Typography variant="body2">{value.created_by}</Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(value.created_at).format("MMM DD, YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      id: "items",
      label: "Item Summary",
      minWidth: 250,
      format: (value, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatTransferSummary(value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {value.length} item{value.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      sortable: true,
      format: (value) => (
        <Chip
          label={value}
          color={getStatusColor(value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: "expected_delivery_date",
      label: "Expected Delivery",
      minWidth: 150,
      sortable: true,
      format: (value) => (
        <Typography variant="body2">
          {dayjs(value).format("MMM DD, YYYY")}
        </Typography>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Stock Transfers
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage stock transfers between locations
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadTransfers}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateTransfer}
            >
              Create Transfer
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Filters
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={clearFilters}
                disabled={loading}
              >
                Clear All
              </Button>
            </Box>

            <Grid container spacing={2}>
              {/* Search */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  placeholder="Search transfers..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Transfer Type */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.transfer_type}
                    onChange={(e) =>
                      handleFilterChange("transfer_type", e.target.value)
                    }
                    label="Type"
                  >
                    <MenuItem value="All">All Types</MenuItem>
                    <MenuItem value={TRANSFER_TYPES.INTERNAL}>
                      Internal
                    </MenuItem>
                    <MenuItem value={TRANSFER_TYPES.INTER_BRANCH}>
                      Inter-Branch
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    label="Status"
                  >
                    <MenuItem value="All">All Status</MenuItem>
                    <MenuItem value={TRANSFER_STATUS.DRAFT}>Draft</MenuItem>
                    <MenuItem value={TRANSFER_STATUS.DISPATCHED}>
                      Dispatched
                    </MenuItem>
                    <MenuItem value={TRANSFER_STATUS.IN_TRANSIT}>
                      In Transit
                    </MenuItem>
                    <MenuItem value={TRANSFER_STATUS.RECEIVED}>
                      Received
                    </MenuItem>
                    <MenuItem value={TRANSFER_STATUS.CANCELLED}>
                      Cancelled
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="From Date"
                  value={
                    filters.date_range?.start
                      ? dayjs(filters.date_range.start)
                      : null
                  }
                  onChange={(date) =>
                    handleDateRangeChange(
                      "start",
                      date ? (date as any).valueOf() : null
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <DatePicker
                  label="To Date"
                  value={
                    filters.date_range?.end
                      ? dayjs(filters.date_range.end)
                      : null
                  }
                  onChange={(date) =>
                    handleDateRangeChange(
                      "end",
                      date ? (date as any).valueOf() : null
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Data Table */}
        <DataTable
          columns={columns}
          rows={transfers}
          totalItems={totalItems}
          page={page}
          pageSize={pageSize}
          sortField={sortField}
          sortOrder={sortOrder}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          loading={loading}
          getRowId={(row) => row._id}
          onDetails={onViewTransfer}
          additionalActions={(row) => (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {canUserReceiveTransfer(row) && (
                <Tooltip title="Receive Transfer">
                  <LoadingButton
                    size="small"
                    onClick={() => handleReceiveTransfer(row)}
                    loading={actionLoading[row._id]}
                    disabled={loading}
                  >
                    <ReceiveIcon fontSize="small" />
                  </LoadingButton>
                </Tooltip>
              )}

              {canUserDispatchTransfer(row) && (
                <Tooltip title="Dispatch Transfer">
                  <LoadingButton
                    size="small"
                    onClick={() => handleDispatchTransfer(row)}
                    loading={actionLoading[row._id]}
                    disabled={loading}
                  >
                    <DispatchIcon fontSize="small" />
                  </LoadingButton>
                </Tooltip>
              )}

              {canUserEditTransfer(row) && (
                <Tooltip title="Edit Transfer">
                  <IconButton
                    size="small"
                    onClick={() => onEditTransfer(row)}
                    disabled={loading}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {row.status === "Draft" && (
                <Tooltip title="Cancel Transfer">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setConfirmDialog({
                        open: true,
                        title: "Cancel Transfer",
                        message:
                          "Are you sure you want to cancel this transfer?",
                        action: () =>
                          handleCancelTransfer(row, "Cancelled by user"),
                      });
                    }}
                    disabled={loading}
                    color="error"
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        />

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setConfirmDialog((prev) => ({ ...prev, open: false }))
              }
              disabled={loading}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={() => {
                confirmDialog.action();
                setConfirmDialog((prev) => ({ ...prev, open: false }));
              }}
              loading={loading}
              variant="contained"
              color="error"
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};
